import React, { useState, useEffect } from 'react';
import { Alert } from 'react-bootstrap';
import axios from 'axios';
import API_BASE_URL from '../config';
import { getEffectiveTokenLimit, getTokensRemaining } from '../utils/planTokens';
import './PlanInfo.css';
import UpgradeModal from './UpgradeModal';

const PlanInfo = ({ userPlan, totalTokensUsed, tokenLimit, trialPeriodEndDate, onPlanSwitched }) => {
  const [switching, setSwitching] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [price, setPrice] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [alertVariant, setAlertVariant] = useState('success');

  useEffect(() => {
    fetchPrice();
  }, []);

  const fetchPrice = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/api/payment/price`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setPrice(response.data);
      }
    } catch (error) {
      console.error('Error fetching price:', error);
      // Set default price if fetch fails
      setPrice({ amountInDollars: '10.00', currency: 'usd' });
    }
  };

  const now = new Date();
  const endDate = trialPeriodEndDate ? new Date(trialPeriodEndDate) : null;
  const daysRemaining = endDate ? Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)) : null;
  const trialExpired = endDate && endDate < now;
  const canSwitchToFree = userPlan === 'trial' && trialExpired;

  const handleSwitchToFree = async () => {
    setSwitching(true);
    setAlertMessage(null);
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setAlertMessage('Please login to switch plans');
        setAlertVariant('warning');
        setSwitching(false);
        return;
      }
      
      // Use relative URL if API_BASE_URL is empty (will go through proxy)
      const apiUrl = API_BASE_URL ? `${API_BASE_URL}/api/user/switch-to-free-plan` : '/api/user/switch-to-free-plan';
      console.log('Switching to free plan, API URL:', apiUrl);
      
      const response = await axios.post(apiUrl, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAlertMessage('Successfully switched to free plan! You now have 1 million tokens available for 1 month.');
        setAlertVariant('success');
        if (onPlanSwitched) {
          onPlanSwitched();
        }
        setTimeout(() => {
          window.location.reload(); // Reload to refresh user data
        }, 2000);
      }
    } catch (error) {
      console.error('Error switching to free plan:', error);
      setAlertMessage(error.response?.data?.error || 'Failed to switch to free plan. Please try again.');
      setAlertVariant('danger');
    } finally {
      setSwitching(false);
      setShowConfirmModal(false);
    }
  };

  const getPlanDisplayName = (plan) => {
    switch (plan) {
      case 'trial': return 'Trial Plan';
      case 'free': return 'Free Plan';
      case 'paid': return 'Paid Plan';
      default: return 'Unknown Plan';
    }
  };

  const planLimit = getEffectiveTokenLimit(userPlan, tokenLimit);
  const tokensRemaining = getTokensRemaining(userPlan, totalTokensUsed, tokenLimit);
  const usagePercentage =
    planLimit !== null && planLimit > 0 ? (totalTokensUsed / planLimit) * 100 : 0;

  return (
    <div className="plan-info-container">
      {alertMessage && (
        <Alert variant={alertVariant} dismissible onClose={() => setAlertMessage(null)} className="mb-3">
          {alertMessage}
        </Alert>
      )}
      <div className="plan-info-header">
        <h3>Current Plan: {getPlanDisplayName(userPlan)}</h3>
        {canSwitchToFree && (
          <button 
            className="switch-plan-btn"
            onClick={() => setShowConfirmModal(true)}
            disabled={switching}
          >
            {switching ? 'Switching...' : 'Switch to Free Plan'}
          </button>
        )}
        {userPlan !== 'paid' && (
          <button 
            className="upgrade-btn"
            onClick={() => setShowUpgradeModal(true)}
          >
            {price ? `Upgrade - $${price.amountInDollars}/mo` : 'Upgrade'}
          </button>
        )}
      </div>

      <div className="plan-info-content">
        <div className="plan-stats">
          <div className="stat-item">
            <label>Token Limit:</label>
            <span className="stat-value">
              {planLimit === undefined
                ? '—'
                : planLimit !== null
                  ? planLimit.toLocaleString()
                  : 'Unlimited'}
            </span>
          </div>
          <div className="stat-item">
            <label>Tokens Used:</label>
            <span className="stat-value">{totalTokensUsed.toLocaleString()}</span>
          </div>
          {tokensRemaining !== undefined && tokensRemaining !== null && planLimit > 0 && (
            <div className="stat-item">
              <label>Tokens Remaining:</label>
              <span
                className={`stat-value ${tokensRemaining < planLimit * 0.1 ? 'warning' : ''}`}
              >
                {tokensRemaining.toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {planLimit !== undefined && planLimit !== null && planLimit > 0 && (
          <div className="token-usage-bar">
            <div className="usage-bar-background">
              <div 
                className="usage-bar-fill"
                style={{ width: `${Math.min(usagePercentage, 100)}%` }}
              />
            </div>
            <div className="usage-percentage">{usagePercentage.toFixed(1)}%</div>
          </div>
        )}

        {userPlan === 'trial' && daysRemaining !== null && (
          <div className="trial-info">
            {trialExpired ? (
              <div className="trial-expired">
                <strong>⚠️ Trial Period Ended</strong>
                <p>Your trial period has ended. Switch to the free plan to continue using the service with 1 million tokens.</p>
              </div>
            ) : (
              <div className="trial-active">
                <strong>⏳ Trial Period Active</strong>
                <p>{daysRemaining > 0 ? `${daysRemaining} days remaining` : 'Trial period ending today'}</p>
              </div>
            )}
          </div>
        )}

        {userPlan === 'free' && (
          <div className="free-plan-info">
            <strong>✓ Free Plan Active</strong>
            <p>You have 1 million tokens available for every month.</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={() => setShowConfirmModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Switch to Free Plan</h3>
            <p>Are you sure you want to switch to the Free Plan?</p>
            <ul style={{ textAlign: 'left', margin: '15px 0' }}>
              <li>You will have 1 million tokens available for 1 month</li>
              <li>This action cannot be undone</li>
            </ul>
            <div className="modal-actions">
              <button 
                className="btn-cancel"
                onClick={() => setShowConfirmModal(false)}
                disabled={switching}
              >
                Cancel
              </button>
              <button 
                className="btn-confirm"
                onClick={handleSwitchToFree}
                disabled={switching}
              >
                {switching ? 'Switching...' : 'Confirm Switch'}
              </button>
            </div>
          </div>
        </div>
      )}

      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        userPlan={userPlan}
      />
    </div>
  );
};

export default PlanInfo;

